import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Logo } from '@/components/layout/Logo'
import { Link } from 'react-router-dom'
import { ArrowRightIcon } from '@untitledui-icons/react/line'
import { OnboardingLanguageSelector } from './ui/OnboardingLanguageSelector'

interface OnboardingLayoutProps {
  children: React.ReactNode
  showHeroPanel?: boolean
  showSignIn?: boolean
  progress?: number
  totalSteps?: number
  wideLayout?: boolean
  fullWidthContent?: boolean
  hideProgressBar?: boolean
}

export function OnboardingLayout({
  children,
  showHeroPanel = true,
  showSignIn = true,
  progress = 1,
  totalSteps = 9,
  wideLayout = false,
  fullWidthContent = false,
  hideProgressBar = false,
}: OnboardingLayoutProps) {
  const { t } = useTranslation()
  const progressPercent = Math.min((progress / totalSteps) * 100, 100)

  // Dynamically load Inter font only for onboarding
  useEffect(() => {
    const id = 'inter-font-link'
    if (!document.getElementById(id)) {
      const link = document.createElement('link')
      link.id = id
      link.rel = 'stylesheet'
      link.href = 'https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap'
      document.head.appendChild(link)
    }
  }, [])

  return (
    // h-dvh, not h-screen: on iOS Safari 100vh is the address-bar-collapsed
    // height, so an overflow-hidden root clips its own bottom out of reach and
    // the inner scrollers size themselves to a box that never overflows.
    <div className="onboarding-layout relative flex h-dvh w-full flex-col overflow-hidden bg-white" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
      {/* Header */}
      <header className="z-50 w-full flex-shrink-0 bg-white">
        <div className={`flex w-full items-center justify-between px-5 py-3 sm:py-4 ${showHeroPanel ? 'lg:pl-10 lg:pr-8' : 'lg:mx-auto lg:max-w-[1360px] lg:px-10'}`}>
          {/* Logo - force light mode colors */}
          <Link to="https://bonnie.ai" target="_blank" className="flex items-center">
            <Logo 
              width={96} 
              primary="#368f8b"
              secondary="#172121"
            />
          </Link>

          {/* Right side actions */}
          <div className="flex items-center gap-3">
            {/* Language selector */}
            <OnboardingLanguageSelector />

            {/* Sign in button - hidden on small mobile */}
            {showSignIn && (
              <Link
                to="/login"
                className="hidden sm:flex cursor-pointer items-center gap-1 rounded-lg border border-[#d5d7da] bg-white px-3 py-2 shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05)] transition-colors hover:bg-[#fafafa]"
              >
                <span className="px-0.5 text-sm font-semibold text-[#414651]">
                  {t('resources.signup.common.sign_in')}
                </span>
                <ArrowRightIcon className="h-5 w-5 text-[#a4a7ae]" />
              </Link>
            )}
          </div>
        </div>

        {/* Progress bar */}
        {!hideProgressBar && (
          <div className="relative h-px w-full bg-[#e9eaeb]">
            <div
              className="absolute left-0 top-0 h-full bg-[#181d27] transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        )}
      </header>

      {/* Main content */}
      <main className="min-h-0 flex-1 overflow-hidden">
        {/* Full width content mode (for confirmation step) */}
        {fullWidthContent ? (
          <div className="flex h-full w-full flex-col overflow-auto">
            {children}
          </div>
        ) : showHeroPanel ? (
          /* Two-column: form left 50%, hero right 50% — matching Figma */
          <div className="grid h-full lg:grid-cols-2">
            {/* Left — form centered in its half */}
            <div className={`flex justify-center overflow-y-auto px-5 pb-8 sm:px-6 ${wideLayout ? 'items-start pt-12' : 'pt-6 sm:pt-10 md:pt-16 lg:pt-[72px]'}`}>
              <div className={wideLayout ? 'flex min-h-full w-full max-w-[1000px] flex-col' : 'flex min-h-full w-full max-w-[400px] flex-col'}>
                {children}
              </div>
            </div>

            {/* Right — hero video pinned to the right half */}
            <div className="relative hidden p-8 pl-0 lg:block">
              <div className="relative h-full w-full overflow-hidden rounded-3xl bg-[#000]">
                {/* Background video */}
                <video
                  className="absolute inset-0 h-full w-full object-cover"
                  src="https://static-files.ams3.digitaloceanspaces.com/bonnie-dashboard/videos/bonnie-website.webm"
                  autoPlay
                  loop
                  muted
                  playsInline
                />

                {/* Dark overlay 80% */}
                <div className="absolute inset-0 bg-black/70" />

                {/* Content */}
                <div className="relative flex h-full flex-col items-center justify-center px-12">
                  {/* Badge */}
                  <div className="mb-8 rounded-full border border-[rgba(255,255,255,0.16)] bg-[rgba(255,255,255,0.08)] px-3 py-1">
                    <span className="text-sm font-medium text-[rgba(255,255,255,0.72)]">
                      Introducing Bonnie.ai
                    </span>
                  </div>

                  {/* Title */}
                  <h2 className="max-w-[477px] text-center text-[60px] font-medium leading-[72px] tracking-[-1.2px] text-white">
                    Your always-on<br />AI teammate.
                  </h2>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* No hero — form centered full width */
          <div className={`flex h-full justify-center overflow-y-auto px-5 pb-8 sm:px-6 ${wideLayout ? 'items-start pt-8' : 'pt-4 sm:pt-6 md:pt-8 lg:pt-10'}`}>
            <div className={wideLayout ? 'flex min-h-full w-full max-w-[1000px] flex-col' : 'flex min-h-full w-full max-w-[400px] flex-col'}>
              {children}
            </div>
          </div>
        )}
      </main>
    </div>
  )
}
